import { useQuery } from "@tanstack/react-query";
import { db } from "@/modules/shared/lib/db-client";
import { useDataAccessLevel } from "./useCurrentTeamMember";
import { useAuth } from "./useAuth";

export interface UserPermission {
  id: string;
  user_id: string;
  feature_key: string;
  is_enabled: boolean;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  expires_at: string | null;
  granted_by: string | null;
  granted_at: string | null;
}

export type PermissionAction = "view" | "create" | "edit" | "delete";

// Hierarchical permission structure: parent -> children
export interface PermissionModule {
  key: string;
  label: string;
  children: string[];
}

export const FEATURE_LABELS: Record<string, string> = {
  // Dashboard
  visao_geral: "Visão Geral",
  
  // Gestão - Clientes
  clientes: "Clientes",
  clientes_profissionais: "Ver Profissionais",
  clientes_checkins: "Ver Performance",
  clientes_nps: "Ver NPS",
  clientes_servicos: "Gerenciar Serviços",
  clientes_responsaveis: "Ver Responsáveis",
  clientes_contatos: "Ver Contatos",
  clientes_ratecard: "Ver Rate Card",
  clientes_contratos: "Ver Contratos",
  
  // Gestão - Profissionais
  profissionais: "Profissionais",
  profissionais_perfil: "Ver Perfil Completo",
  profissionais_alocacoes: "Ver Alocações",
  profissionais_cultura: "Ver Avaliação Cultura",
  profissionais_contrato: "Ver Contrato",
  profissionais_pdi: "Ver PDI",
  
  // Gestão - Alocações
  alocacoes: "Alocações",
  alocacoes_criar: "Criar Alocações",
  alocacoes_editar: "Editar Alocações",
  alocacoes_encerrar: "Encerrar Alocações",
  alocacoes_evidencias: "Gerenciar Evidências",
  alocacoes_followups: "Gerenciar Follow-ups",
  
  // Gestão - Vagas
  vagas: "Vagas Solicitadas",
  vagas_criar: "Criar Vagas",
  vagas_editar: "Editar Vagas",
  vagas_candidatos: "Gerenciar Candidatos",
  
  // Gestão - Recrutamento
  recrutamento: "Recrutamento & Seleção",
  recrutamento_assessments: "Gerenciar Assessments",
  recrutamento_questoes: "Banco de Questões",
  recrutamento_candidatos: "Avaliar Candidatos",
  
  // Gestão - Squad
  squad: "Serviço Gerenciado",
  squad_contratos: "Ver Contratos Squad",
  squad_dashboard: "Ver Dashboard",
  squad_profissionais: "Gerenciar Profissionais",
  squad_valores: "Financeiro",
  
  // Gestão - Projetos
  projetos: "Projetos",
  
  // SpecQA
  specqa: "SpecQA - QA Automatizada",
  
  // Acompanhamento - Performance
  checkins: "Performance",
  checkins_realizar: "Realizar Avaliações",
  checkins_historico: "Ver Histórico",
  
  // Acompanhamento - Planos de Ação
  planos_acao: "Planos de Ação",
  planos_acao_criar: "Criar Planos",
  planos_acao_editar: "Editar Planos",
  
  // Acompanhamento - PDI
  pdi: "PDI & Feedbacks",
  pdi_sessoes: "Gerenciar Sessões",
  pdi_acordos: "Gerenciar Acordos",
  pdi_evolucao: "Ver Evolução",
  
  // Acompanhamento - Cultura
  cultura: "Cultura Open",
  cultura_avaliar: "Realizar Avaliações",
  cultura_dashboard: "Ver Dashboard",
  cultura_config: "Configurar Critérios",
  
  // Acompanhamento - Resumo
  resumo_mensal: "Resumo Mensal",
  
  // Timesheet
  timesheet: "Timesheet - Minhas Horas",
  timesheet_weekly: "Timesheet - Semanal",
  timesheet_calendar: "Timesheet - Calendário",
  timesheet_all_times: "Timesheet - All Times",
  timesheet_aprovar: "Aprovar Lançamentos",
  
  // Resultados
  painel_gestores: "Painel Gestores",
  
  
  // Comercial
  pipeline: "Pipeline Comercial",
  minha_carteira: "Minha Carteira",
  
  financeiro: "Financeiro",
  financeiro_comissoes: "Ver Comissões",
  financeiro_comissoes_aprovar: "Aprovar Comissões",
  financeiro_relatorios: "Ver Relatórios",
  reembolsos: "Reembolsos",
  reembolsos_aprovar: "Aprovar Reembolsos",
  reembolsos_pagar: "Marcar como Pago",
  rate_card: "Rate Card",
  rate_card_editar: "Editar Rate Card",
  calculadora: "Calculadora Comercial",
  
  
  // Campeonato
  opencode: "OpenCode Championship",
  opencode_admin: "Administrar Campeonato",

  // Sistema
  configuracoes: "Configurações",
  equipe: "Gestão de Equipe",
  equipe_convidar: "Convidar Membros",
  equipe_permissoes: "Gerenciar Permissões",
};

export { ROLE_LABELS, getRoleLabel } from "@/modules/shared/lib/roleLabels";

// Define hierarchical structure: which permissions are children of which parent
export const PERMISSION_HIERARCHY: PermissionModule[] = [
  {
    key: "visao_geral",
    label: "Dashboard",
    children: [],
  },
  {
    key: "clientes",
    label: "Clientes",
    children: [
      "clientes_profissionais",
      "clientes_checkins",
      "clientes_nps",
      "clientes_servicos",
      "clientes_responsaveis",
      "clientes_contatos",
      "clientes_ratecard",
      "clientes_contratos",
    ],
  },
  {
    key: "profissionais",
    label: "Profissionais",
    children: [
      "profissionais_perfil",
      "profissionais_alocacoes",
      "profissionais_cultura",
      "profissionais_contrato",
      "profissionais_pdi",
    ],
  },
  {
    key: "alocacoes",
    label: "Alocações",
    children: [
      "alocacoes_criar",
      "alocacoes_editar",
      "alocacoes_encerrar",
      "alocacoes_evidencias",
      "alocacoes_followups",
    ],
  },
  {
    key: "vagas",
    label: "Vagas Solicitadas",
    children: [
      "vagas_criar",
      "vagas_editar",
      "vagas_candidatos",
    ],
  },
  {
    key: "recrutamento",
    label: "Recrutamento & Seleção",
    children: [
      "recrutamento_assessments",
      "recrutamento_questoes",
      "recrutamento_candidatos",
    ],
  },
  {
    key: "squad",
    label: "Squads",
    children: [
      "squad_contratos",
      "squad_dashboard",
      "squad_profissionais",
      "squad_valores",
    ],
  },
  {
    key: "projetos",
    label: "Projetos",
    children: [],
  },
  {
    key: "specqa",
    label: "SpecQA",
    children: [],
  },
  {
    key: "checkins",
    label: "Avaliações",
    children: [
      "checkins_realizar",
      "checkins_historico",
    ],
  },
  {
    key: "planos_acao",
    label: "Planos de Ação",
    children: [
      "planos_acao_criar",
      "planos_acao_editar",
    ],
  },
  {
    key: "pdi",
    label: "PDI & Feedbacks",
    children: [
      "pdi_sessoes",
      "pdi_acordos",
      "pdi_evolucao",
    ],
  },
  {
    key: "cultura",
    label: "Cultura Open",
    children: [
      "cultura_avaliar",
      "cultura_dashboard",
      "cultura_config",
    ],
  },
  {
    key: "resumo_mensal",
    label: "Resumo Mensal",
    children: [],
  },
  {
    key: "timesheet",
    label: "Timesheet",
    children: [
      "timesheet_weekly",
      "timesheet_calendar",
      "timesheet_all_times",
      "timesheet_aprovar",
    ],
  },
  {
    key: "painel_gestores",
    label: "Painel Gestores",
    children: [],
  },
  {
    key: "pipeline",
    label: "Pipeline Comercial",
    children: [],
  },
  {
    key: "minha_carteira",
    label: "Minha Carteira",
    children: [],
  },
  {
    key: "minhas_metas",
    label: "Minhas Metas",
    children: [],
  },
  {
    key: "financeiro",
    label: "Financeiro",
    children: [
      "financeiro_comissoes",
      "financeiro_comissoes_aprovar",
      "financeiro_relatorios",
    ],
  },
  {
    key: "reembolsos",
    label: "Reembolsos",
    children: [
      "reembolsos_aprovar",
      "reembolsos_pagar",
    ],
  },
  {
    key: "rate_card",
    label: "Rate Card",
    children: [
      "rate_card_editar",
    ],
  },
  {
    key: "calculadora",
    label: "Calculadora Comercial",
    children: [],
  },
  {
    key: "metas_individuais",
    label: "Metas Individuais",
    children: [],
  },
  {
    key: "opencode",
    label: "OpenCode Championship",
    children: [
      "opencode_admin",
    ],
  },
  {
    key: "configuracoes",
    label: "Configurações",
    children: [],
  },
  {
    key: "equipe",
    label: "Gestão de Equipe",
    children: [
      "equipe_convidar",
      "equipe_permissoes",
    ],
  },
];

// Group modules by category for accordion display
export const PERMISSION_CATEGORIES = {
  gestao: {
    label: "Gestão",
    modules: ["clientes", "profissionais", "alocacoes", "equipe"],
  },
  recrutamento: {
    label: "Recrutamento",
    modules: ["vagas", "recrutamento"],
  },
  operacoes: {
    label: "Operações",
    modules: ["projetos", "squad"],
  },
  acompanhamento: {
    label: "Acompanhamento",
    modules: ["checkins", "planos_acao", "pdi", "cultura", "resumo_mensal", "timesheet"],
  },
  resultados: {
    label: "Resultados",
    modules: ["painel_gestores", "metas", "metas_individuais", "minhas_metas"],
  },
  comercial: {
    label: "Comercial",
    modules: ["pipeline", "minha_carteira", "rate_card", "calculadora"],
  },
  financeiro: {
    label: "Financeiro",
    modules: ["financeiro", "reembolsos"],
  },
  campeonato: {
    label: "Campeonato",
    modules: ["opencode"],
  },
  sistema: {
    label: "Sistema",
    modules: ["visao_geral", "configuracoes"],
  },
};

// Legacy FEATURE_GROUPS for backwards compatibility
export const FEATURE_GROUPS = {
  dashboard: ["visao_geral"],
  gestao: ["clientes", "profissionais", "alocacoes", "equipe", "equipe_convidar", "equipe_permissoes"],
  clientes_granular: [
    "clientes_profissionais",
    "clientes_checkins",
    "clientes_nps",
    "clientes_servicos",
    "clientes_responsaveis",
    "clientes_contatos",
    "clientes_ratecard",
    "clientes_contratos",
  ],
  profissionais_granular: [
    "profissionais_perfil",
    "profissionais_alocacoes",
    "profissionais_cultura",
    "profissionais_contrato",
    "profissionais_pdi",
  ],
  alocacoes_granular: [
    "alocacoes_criar",
    "alocacoes_editar",
    "alocacoes_encerrar",
    "alocacoes_evidencias",
    "alocacoes_followups",
  ],
  recrutamento: ["vagas", "recrutamento"],
  vagas_granular: [
    "vagas_criar",
    "vagas_editar",
    "vagas_candidatos",
  ],
  recrutamento_granular: [
    "recrutamento_assessments",
    "recrutamento_questoes",
    "recrutamento_candidatos",
  ],
  operacoes: ["projetos", "squad"],
  squad_granular: [
    "squad_contratos",
    "squad_dashboard",
    "squad_profissionais",
    "squad_valores",
  ],
  acompanhamento: ["checkins", "planos_acao", "pdi", "cultura", "resumo_mensal", "timesheet"],
  checkins_granular: ["checkins_realizar", "checkins_historico"],
  planos_acao_granular: ["planos_acao_criar", "planos_acao_editar"],
  pdi_granular: ["pdi_sessoes", "pdi_acordos", "pdi_evolucao"],
  cultura_granular: ["cultura_avaliar", "cultura_dashboard", "cultura_config"],
  timesheet_granular: ["timesheet_weekly", "timesheet_calendar", "timesheet_all_times", "timesheet_aprovar"],
  resultados: ["painel_gestores"],
  comercial: ["pipeline", "minha_carteira", "rate_card", "rate_card_editar", "calculadora"],
  financeiro: ["financeiro", "financeiro_comissoes", "financeiro_comissoes_aprovar", "financeiro_relatorios", "reembolsos", "reembolsos_aprovar", "reembolsos_pagar"],
  sistema: ["visao_geral", "configuracoes"],
};

// Helper to get all children of a parent permission
export function getChildPermissions(parentKey: string): string[] {
  const module = PERMISSION_HIERARCHY.find(m => m.key === parentKey);
  return module?.children || [];
}

// Helper to check if a key is a parent permission
export function isParentPermission(key: string): boolean {
  return PERMISSION_HIERARCHY.some(m => m.key === key);
}

// Helper to get parent of a child permission
export function getParentPermission(childKey: string): string | null {
  for (const module of PERMISSION_HIERARCHY) {
    if (module.children.includes(childKey)) {
      return module.key;
    }
  }
  return null;
}

// Helper to check if a permission is expired
function isPermissionExpired(permission: UserPermission): boolean {
  if (!permission.expires_at) return false;
  return new Date(permission.expires_at) < new Date();
}

// Helper to check a specific CRUD action on a permission
function checkPermissionAction(permission: UserPermission | undefined, action: PermissionAction): boolean {
  if (!permission) return false;
  if (isPermissionExpired(permission)) return false;
  
  switch (action) {
    case "view": return permission.can_view || permission.is_enabled;
    case "create": return permission.can_create;
    case "edit": return permission.can_edit;
    case "delete": return permission.can_delete;
    default: return permission.can_view || permission.is_enabled;
  }
}

// Hook to get current user's permissions
function useCurrentUserPermissions() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ["user-permissions", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await db
        .from("user_permissions")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      return data as UserPermission[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to check if permissions are still loading
export function usePermissionsLoading(): boolean {
  const { isReady } = useDataAccessLevel();
  const { user } = useAuth();
  const { isLoading } = useQuery({
    queryKey: ["user-permissions", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await db
        .from("user_permissions")
        .select("*")
        .eq("user_id", user.id);
      if (error) throw error;
      return data as UserPermission[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  return !isReady || isLoading;
}

export function useCanAccess(featureKey: string, action: PermissionAction = "view"): boolean {
  const { teamMember, isReady, hasFullAccess, isAdmin } = useDataAccessLevel();
  const { data: userPermissions, isLoading } = useCurrentUserPermissions();

  // While loading, assume no access to prevent flash of forbidden content
  if (!isReady || isLoading) return false;

  // Users without team_member (like super admins) have full access
  if (hasFullAccess && !teamMember) return true;

  // Admins always have full access
  if (isAdmin) return true;

  // Check if this is a child permission - if parent is disabled, child is also disabled
  const parentKey = getParentPermission(featureKey);
  if (parentKey) {
    const parentPermission = userPermissions?.find((p) => p.feature_key === parentKey);
    if (parentPermission && !checkPermissionAction(parentPermission, "view")) {
      return false;
    }
  }

  // Check user-specific permissions
  const permission = userPermissions?.find((p) => p.feature_key === featureKey);
  
  // If no custom permission set, default to DISABLED (false)
  return checkPermissionAction(permission, action);
}

export function useUserPermissions(): Record<string, boolean> {
  const { teamMember, isReady, hasFullAccess, isAdmin } = useDataAccessLevel();
  const { data: userPermissions, isLoading } = useCurrentUserPermissions();

  // Default all ENABLED while loading to prevent flash of hidden content
  // The ProtectedRoute will handle unauthorized access
  if (!isReady || isLoading) {
    return Object.keys(FEATURE_LABELS).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }

  // Full access users get everything
  if (hasFullAccess && !teamMember) {
    return Object.keys(FEATURE_LABELS).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }

  // Admins get everything
  if (isAdmin) {
    return Object.keys(FEATURE_LABELS).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
  }

  const result: Record<string, boolean> = {};

  Object.keys(FEATURE_LABELS).forEach((key) => {
    // Check if parent is disabled
    const parentKey = getParentPermission(key);
    if (parentKey) {
      const parentPermission = userPermissions?.find((p) => p.feature_key === parentKey);
      if (parentPermission && !checkPermissionAction(parentPermission, "view")) {
        result[key] = false;
        return;
      }
    }
    
    const permission = userPermissions?.find((p) => p.feature_key === key);
    // Default to DISABLED if no custom permission set
    result[key] = checkPermissionAction(permission, "view");
  });

  return result;
}

// Hook for granular CRUD permission checks
export function useCrudPermissions(featureKey: string) {
  const canView = useCanAccess(featureKey, "view");
  const canCreate = useCanAccess(featureKey, "create");
  const canEdit = useCanAccess(featureKey, "edit");
  const canDelete = useCanAccess(featureKey, "delete");

  return { canView, canCreate, canEdit, canDelete };
}
